import React, { useEffect, useState } from 'react'
import ChartPreviewWithOptions from '../ChartPreviewWithOptions'
import TextStylePanel from '../MvpAssistant/TextStylePanel'
import BrandThemePanel from '../MvpAssistant/BrandThemePanel'
import AxisStylePanel from '../MvpAssistant/AxisStylePanel'
import LayoutStylePanel from '../MvpAssistant/LayoutStylePanel'
import AxisDisplayFormatControl from './AxisDisplayFormatControl'
import { getStyleGroupFilter } from '../../services/styleGroupFilter'
import { useDataContext } from '../../contexts/DataContext'
import { useChartContext } from '../../contexts/ChartContext'
import { useUIContext } from '../../contexts/UIContext'

const MOBILE_APPEARANCE_SECTION_STORAGE_KEY =
  'lugsail.style.mobileAppearanceSection'

function MobileAccordionSection({
  title,
  subtitle = null,
  open = false,
  badge = null,
  onToggle,
  children,
}) {
  return (
    <div className="mobile-style-accordion">
      <button
        type="button"
        className="mobile-style-accordion__summary"
        aria-expanded={open}
        onClick={onToggle}
      >
        <div className="mobile-style-accordion__title-row">
          <span>{title}</span>
          {badge}
        </div>
        {subtitle ? (
          <div className="mobile-style-accordion__subtitle">{subtitle}</div>
        ) : null}
      </button>
      {open && <div className="mobile-style-accordion__content">{children}</div>}
    </div>
  )
}

export default function StyleSidebarContent() {
  const { data, largeDatasetInfo } = useDataContext()

  const {
    currentChart,
    brandTheme,
    setBrandTheme,
    savedBrandProfiles,
    applyBrandProfile,
    saveBrandProfile,
    deleteBrandProfile,
    applyPaletteToChartColors,
    useThemePaletteForChartColors,
    chartColorOptionMeta,
    paletteApplyDisabledReason,
    chartColorSyncMode,
    chartPaletteDirty,
    dismissBrandPaletteUpdatePrompt,
    mapping,
    visualOptions,
    setVisualOptions,
    rawViz,
    setRawViz,
    setMappingLoading,
    axisDisplayControls,
  } = useChartContext()

  const { activeStyleTab, previewRendererMode } = useUIContext()

  const [isMobileLayout, setIsMobileLayout] = useState(false)
  const [mobileAppearanceSection, setMobileAppearanceSection] = useState('chart-palette')

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)')
    const handleChange = () => setIsMobileLayout(mediaQuery.matches)
    handleChange()

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !isMobileLayout) {
      return
    }

    const storedSection = window.sessionStorage.getItem(
      MOBILE_APPEARANCE_SECTION_STORAGE_KEY
    )

    if (storedSection) {
      setMobileAppearanceSection(storedSection)
    }
  }, [isMobileLayout])

  useEffect(() => {
    if (typeof window === 'undefined' || !isMobileLayout) {
      return
    }

    if (mobileAppearanceSection) {
      window.sessionStorage.setItem(
        MOBILE_APPEARANCE_SECTION_STORAGE_KEY,
        mobileAppearanceSection
      )
      return
    }

    window.sessionStorage.removeItem(MOBILE_APPEARANCE_SECTION_STORAGE_KEY)
  }, [isMobileLayout, mobileAppearanceSection])

  if (!data || !currentChart) {
    return null
  }

  if (activeStyleTab === 'brand') {
    const nodesLinksContent =
      chartColorOptionMeta.hasChartColorScale &&
      chartColorOptionMeta.hasConfigurableColorDomain ? (
        <ChartPreviewWithOptions
          chart={currentChart}
          dataset={data.dataset}
          dataTypes={data.dataTypes}
          mapping={mapping}
          visualOptions={visualOptions}
          setVisualOptions={setVisualOptions}
          setRawViz={setRawViz}
          datasetRowCount={largeDatasetInfo.rowCount}
          previewRendererMode={previewRendererMode}
          setMappingLoading={setMappingLoading}
          brandTheme={brandTheme}
          chartColorSyncMode={chartColorSyncMode}
          hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
          showPreview={false}
          optionsClassName="workflow-panel workflow-panel-left workflow-panel-nested"
          optionGroupFilter={getStyleGroupFilter('brand')}
          hideOptionGroupChrome
          optionsEmptyState="No color options are available for the current chart."
        />
      ) : (
        <div className="workflow-panel workflow-panel-left workflow-panel-nested">
          <div className="text-muted small">
            {chartColorOptionMeta.hasChartColorScale
              ? 'Map a field to Color in the Mapping step to edit per-category chart colors here. Until then, the chart uses the palette above.'
              : 'This chart does not expose separate per-category color controls. The palette above styles the chart directly.'}
          </div>
        </div>
      )

    return (
      <div className="workflow-panel workflow-panel-left">
        <BrandThemePanel
          theme={brandTheme}
          setTheme={setBrandTheme}
          savedProfiles={savedBrandProfiles}
          onApplyProfile={applyBrandProfile}
          onSaveProfile={saveBrandProfile}
          onDeleteProfile={deleteBrandProfile}
          onApplyPaletteToChart={applyPaletteToChartColors}
          onUseThemePaletteForChart={useThemePaletteForChartColors}
          hasChartColorScale={chartColorOptionMeta.hasChartColorScale}
          hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
          hasConfigurableColorDomain={chartColorOptionMeta.hasConfigurableColorDomain}
          colorDimensionLabel={chartColorOptionMeta.colorDimensionLabel}
          canApplyPaletteToChart={!paletteApplyDisabledReason}
          paletteApplyDisabledReason={paletteApplyDisabledReason}
          chartColorSyncMode={chartColorSyncMode}
          chartPaletteDirty={chartPaletteDirty}
          onDismissPaletteUpdatePrompt={dismissBrandPaletteUpdatePrompt}
          isMobileLayout={isMobileLayout}
          useAccordionLayout
          mobileOpenSection={mobileAppearanceSection}
          onToggleMobileSection={(sectionId) =>
            setMobileAppearanceSection((current) =>
              current === sectionId ? '' : sectionId
            )
          }
        />
        <div className="appearance-native-divider">Chart color controls</div>
        <MobileAccordionSection
          title="Nodes / Links"
          subtitle="Choose how dataset values are colored in the chart."
          open={mobileAppearanceSection === 'nodes-links'}
          onToggle={() =>
            setMobileAppearanceSection((current) =>
              current === 'nodes-links' ? '' : 'nodes-links'
            )
          }
          badge={
            chartColorSyncMode === 'manual' ? (
              <span className="appearance-status-pill">Customized</span>
            ) : null
          }
        >
          {nodesLinksContent}
        </MobileAccordionSection>
      </div>
    )
  }

  if (activeStyleTab === 'labels') {
    const labelHandlingContent = (
      <>
        <AxisDisplayFormatControl
          axisControls={axisDisplayControls}
          visualOptions={visualOptions}
          setVisualOptions={setVisualOptions}
        />
        <ChartPreviewWithOptions
          chart={currentChart}
          dataset={data.dataset}
          dataTypes={data.dataTypes}
          mapping={mapping}
          visualOptions={visualOptions}
          setVisualOptions={setVisualOptions}
          setRawViz={setRawViz}
          datasetRowCount={largeDatasetInfo.rowCount}
          previewRendererMode={previewRendererMode}
          setMappingLoading={setMappingLoading}
          brandTheme={brandTheme}
          chartColorSyncMode={chartColorSyncMode}
          hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
          showPreview={false}
          optionsClassName="workflow-panel workflow-panel-left workflow-panel-nested"
          optionGroupFilter={getStyleGroupFilter(activeStyleTab)}
          modernOptionGroupChrome
          optionsEmptyState="No chart options from this category are available for the current chart."
        />
      </>
    )

    return (
      <div className="workflow-panel workflow-panel-left">
        <TextStylePanel
          theme={brandTheme}
          setTheme={setBrandTheme}
          supportsAxisLabelHandling={axisDisplayControls?.hasControls}
          rawViz={rawViz}
        >
          {labelHandlingContent}
        </TextStylePanel>
      </div>
    )
  }

  return (
    <div className="workflow-panel workflow-panel-left">
      <ChartPreviewWithOptions
        chart={currentChart}
        dataset={data.dataset}
        dataTypes={data.dataTypes}
        mapping={mapping}
        visualOptions={visualOptions}
        setVisualOptions={setVisualOptions}
        setRawViz={setRawViz}
        datasetRowCount={largeDatasetInfo.rowCount}
        previewRendererMode={previewRendererMode}
        setMappingLoading={setMappingLoading}
        brandTheme={brandTheme}
        chartColorSyncMode={chartColorSyncMode}
        hasMappedColorField={chartColorOptionMeta.hasMappedColorField}
        showPreview={false}
        optionsClassName="workflow-panel workflow-panel-left workflow-panel-nested"
        optionGroupFilter={getStyleGroupFilter(activeStyleTab)}
        modernOptionGroupChrome
        optionsEmptyState="No chart options from this category are available for the current chart."
        axisGroupContent={
          <AxisStylePanel
            visualOptions={visualOptions}
            setVisualOptions={setVisualOptions}
            supportsAxisTitles={axisDisplayControls?.hasControls}
            supportsAxisGuides={axisDisplayControls?.hasControls}
            supportsAxisDomainLine={axisDisplayControls?.hasControls}
          />
        }
        chartGroupContent={
          <LayoutStylePanel
            theme={brandTheme}
            setTheme={setBrandTheme}
            currentChart={currentChart}
            visualOptions={visualOptions}
            setVisualOptions={setVisualOptions}
          />
        }
      />
    </div>
  )
}
